import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Icons
const KeyIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const SetupLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [step, setStep] = useState('loading'); // loading, form, success, error

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid setup link. No token provided.');
        setStep('error');
        setLoading(false);
        return;
      }

      try {
        // Find user by auth_invite_token
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, first_name, email, auth_invite_token, auth_invite_expires')
          .eq('auth_invite_token', token)
          .single();

        if (userError || !userData) {
          setError('Invalid or expired setup link.');
          setStep('error');
          setLoading(false);
          return;
        }

        // Check if expired
        if (userData.auth_invite_expires && new Date(userData.auth_invite_expires) < new Date()) {
          setError('This setup link has expired. Ask your parent to send a new one.');
          setStep('error');
          setLoading(false);
          return;
        }

        setUserInfo(userData);
        setStep('form');
      } catch (err) {
        console.error('Error validating token:', err);
        setError('Something went wrong. Please try again.');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validate passwords
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      // Call Edge Function to create auth user
      const { data, error } = await supabase.functions.invoke('invite-child-auth', {
        body: {
          action: 'confirm-auth',
          token: token,
          password: password,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setStep('success');
      } else {
        throw new Error(data?.error || 'Failed to create login');
      }
    } catch (err) {
      console.error('Error creating login:', err);
      setFormError(err.message || 'Failed to create login. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle login redirect
  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Loading state
  if (loading || step === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Validating setup link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <XIcon />
          </div>
          <h1 style={styles.title}>Invalid Link</h1>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.button} onClick={() => navigate('/')}>
            Go to CardChase
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <CheckIcon />
          </div>
          <h1 style={styles.title}>You're All Set!</h1>
          <p style={styles.subtitle}>
            Your login has been created. You can now sign in with your email and password.
          </p>
          <div style={styles.emailBox}>
            <span style={styles.emailLabel}>Your email:</span>
            <span style={styles.emailValue}>{userInfo?.email}</span>
          </div>
          <button style={styles.button} onClick={handleGoToLogin}>
            Sign In Now
          </button>
        </div>
      </div>
    );
  }

  // Password form
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconBox}>
          <KeyIcon />
        </div>
        <h1 style={styles.title}>
          Hey {userInfo?.first_name || 'there'}!
        </h1>
        <p style={styles.subtitle}>
          Create a password to set up your own CardChase login.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputWrapper}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Create password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              minLength={6}
              required
            />
            <button
              type="button"
              style={styles.eyeButton}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={styles.input}
            minLength={6}
            required
          />

          <p style={styles.hint}>Password must be at least 6 characters</p>

          {formError && <p style={styles.formError}>{formError}</p>}

          <button
            type="submit"
            style={{ ...styles.button, opacity: submitting ? 0.5 : 1 }}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create My Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #2a2a2a',
    borderTopColor: '#8b5cf6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: '#6a6a6a',
    fontSize: '14px',
  },
  card: {
    backgroundColor: '#151515',
    borderRadius: '16px',
    border: '1px solid #2a2a2a',
    padding: '32px 24px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
  },
  iconBox: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8b5cf6',
    margin: '0 auto 20px',
  },
  successIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#22c55e',
    margin: '0 auto 20px',
  },
  errorIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
    margin: '0 auto 20px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#8a8a8a',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
  },
  errorText: {
    color: '#8a8a8a',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    paddingRight: '48px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#6a6a6a',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hint: {
    color: '#6a6a6a',
    fontSize: '12px',
    textAlign: 'left',
    margin: '0',
  },
  formError: {
    color: '#ef4444',
    fontSize: '13px',
    textAlign: 'left',
    margin: '4px 0 0 0',
  },
  button: {
    padding: '14px 24px',
    backgroundColor: '#8b5cf6',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  emailBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '16px',
    backgroundColor: '#1a1a1a',
    borderRadius: '10px',
    marginBottom: '20px',
  },
  emailLabel: {
    color: '#6a6a6a',
    fontSize: '12px',
  },
  emailValue: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '500',
  },
};

export default SetupLogin;
